import {
  schoolMaterialMutationResponseSchema,
  schoolMaterialsResponseSchema,
  type SchoolMaterialsQuery,
} from "@kindtutor/api-contract";
import {
  localizedApiErrorSchema as apiErrorSchema,
  throwApiError,
} from "@kindtutor/auth-client";

import { apiBaseURL, apiFetch } from "../auth-client";

export async function fetchSchoolMaterials(
  schoolId: string,
  input: Partial<SchoolMaterialsQuery> = {},
) {
  const query = new URLSearchParams();
  if (input.q) query.set("q", input.q);
  if (input.schoolSubjectId)
    query.set("schoolSubjectId", input.schoolSubjectId);
  if (input.status) query.set("status", input.status);
  if (input.sort) query.set("sort", input.sort);
  if (input.direction) query.set("direction", input.direction);
  if (input.cursor) query.set("cursor", input.cursor);
  if (input.limit) query.set("limit", String(input.limit));
  const suffix = query.size ? `?${query.toString()}` : "";
  const response = await apiFetch(
    `${apiBaseURL}/v1/schools/${encodeURIComponent(schoolId)}/materials${suffix}`,
    { credentials: "include", headers: { Accept: "application/json" } },
  );
  if (!response.ok)
    await throwApiError(response, "Nie udało się wczytać materiałów.");
  return schoolMaterialsResponseSchema.parse(await response.json());
}

export async function fetchAllSchoolMaterials(schoolId: string) {
  const materials = [];
  let cursor: string | undefined;
  for (let pageNumber = 0; pageNumber < 100; pageNumber += 1) {
    const page = await fetchSchoolMaterials(schoolId, { cursor, limit: 100 });
    materials.push(...page.materials);
    if (!page.page.hasMore || !page.page.nextCursor) {
      return {
        ...page,
        materials,
        page: { ...page.page, hasMore: false, nextCursor: null },
      };
    }
    cursor = page.page.nextCursor;
  }
  throw new Error("Lista materiałów ma zbyt wiele stron do wczytania.");
}

export async function uploadSchoolMaterial(
  schoolId: string,
  schoolSubjectId: string,
  input: { title: string; file: File; seriesId?: string },
) {
  const query = new URLSearchParams({
    title: input.title,
    filename: input.file.name,
  });
  if (input.seriesId) query.set("seriesId", input.seriesId);
  const response = await apiFetch(
    `${apiBaseURL}/v1/schools/${encodeURIComponent(schoolId)}/school-subjects/${encodeURIComponent(schoolSubjectId)}/materials?${query.toString()}`,
    {
      method: "POST",
      credentials: "include",
      headers: {
        Accept: "application/json",
        "Content-Type": input.file.type || "application/octet-stream",
      },
      body: input.file,
    },
  );
  const body: unknown = await response.json();
  if (!response.ok) {
    const error = apiErrorSchema.safeParse(body);
    throw new Error(
      error.success
        ? error.data.error.message
        : "Nie udało się przesłać materiału.",
    );
  }
  return schoolMaterialMutationResponseSchema.parse(body);
}

export async function approveSchoolMaterial(
  schoolId: string,
  materialId: string,
) {
  const response = await apiFetch(
    `${apiBaseURL}/v1/schools/${encodeURIComponent(schoolId)}/materials/${encodeURIComponent(materialId)}/approve`,
    {
      method: "POST",
      credentials: "include",
      headers: { Accept: "application/json" },
    },
  );
  const body: unknown = await response.json();
  if (!response.ok) {
    const error = apiErrorSchema.safeParse(body);
    throw new Error(
      error.success
        ? error.data.error.message
        : "Nie udało się zatwierdzić materiału.",
    );
  }
  return schoolMaterialMutationResponseSchema.parse(body);
}

export async function reindexSchoolMaterial(
  schoolId: string,
  materialId: string,
) {
  const response = await apiFetch(
    `${apiBaseURL}/v1/schools/${encodeURIComponent(schoolId)}/materials/${encodeURIComponent(materialId)}/reindex`,
    {
      method: "POST",
      credentials: "include",
      headers: { Accept: "application/json" },
    },
  );
  const body: unknown = await response.json();
  if (!response.ok) {
    const error = apiErrorSchema.safeParse(body);
    throw new Error(
      error.success
        ? error.data.error.message
        : "Nie udało się uruchomić ponownego indeksowania.",
    );
  }
  return schoolMaterialMutationResponseSchema.parse(body);
}

export function getSchoolMaterialDownloadUrl(
  schoolId: string,
  materialId: string,
) {
  return `${apiBaseURL}/v1/schools/${encodeURIComponent(schoolId)}/materials/${encodeURIComponent(materialId)}/content`;
}

export async function deleteSchoolMaterial(
  schoolId: string,
  materialId: string,
) {
  const response = await apiFetch(
    `${apiBaseURL}/v1/schools/${encodeURIComponent(schoolId)}/materials/${encodeURIComponent(materialId)}`,
    { method: "DELETE", credentials: "include" },
  );
  if (response.ok) return;
  const body: unknown = await response.json();
  const error = apiErrorSchema.safeParse(body);
  throw new Error(
    error.success
      ? error.data.error.message
      : "Nie udało się usunąć materiału.",
  );
}
